import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  associationApi,
  managementCompanyApi,
  orderApi,
  propertyApi,
} from "@/services/api";
import { OrderDocument, DocumentOutputSection, Property } from "@/models";
import { documentTypeApi } from "@/services/api";
import { config } from "@/config";
import { Liquid } from "liquidjs";
import { supabase } from "@/integrations/supabase/client";
import { resolveStorageImagesIn } from "@/utils/storageRefs";
import { formatDate } from "@/forms/fannie-mae-1076/fill";
import { fillerFor } from "@/forms/registry";
import { findPdfFormTemplate, type PdfFormTemplate } from "@/utils/pdfFormTemplate";
import { EMAIL_LOGO_URL } from '@/utils/brandAssets';

interface TemplateContent {
  layout?: string;
  pages: string[];
}

// Page Settings Interfaces
interface BorderSettings {
  enabled: boolean;
  style: "solid";
  width: string;
  color: string;
}

interface FooterSettings {
  enabled: boolean;
  text: string;
  alignment: "left" | "center" | "right";
  fontSize: string;
}

// Generic page info element interface
interface PageInfoElement {
  text: string;
  alignment: "left" | "center" | "right";
  fontSize: string;
}

interface PageSettings {
  border?: BorderSettings;
  footer?: FooterSettings;
  pageInfo?: PageInfoElement[];
}

interface ManagementCompanyData {
  id: number;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
  email?: string;
  logo?: string;
}

interface AssociationData {
  id: number;
  name: string;
  management_company_id: number;
  billing_address?: string;
  billing_city?: string;
  billing_state?: string;
  billing_zip?: string;
  phone?: string;
  email?: string;
}

interface PropertyData {
  id: number;
  address: string;
  city?: string;
  state?: string;
  zip_code?: string;
  suite_unit?: string;
  association_id: number;
  latitude?: number;
  longitude?: number;
}

interface OrderData {
  id: number;
  association_id: number;
  property_id?: number;
  customer_id: number;
  created_at: string;
  status: string;
  processing_type?: string;
  expected_delivery_date: string;
  items: Array<{
    id: number;
    label: string;
    amount: number;
    subtotal_amount: number;
    quantity: number;
    item_type: string;
  }>;
  at_closing_items: Array<{
    id: number;
    label: string;
    amount: number;
    subtotal_amount: number;
    quantity: number;
    item_type: string;
    apportionments?: Array<{
      id: number;
      party_type: string;
      percentage: number;
      amount: number;
    }>;
  }>;
}

interface TemplateVariables {
  document: {
    title: string;
    name: string;
    date: string;
    issuance_date: string;
  };
  association: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
    phone: string;
    email: string;
  };
  management: {
    name: string;
    logo: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
    phone: string;
    email: string;
  };
  property: Property;
  order: {
    id: number;
    created_at: string;
    estimated_closing_date: string;
    expected_delivery_date: string;
    completion_date: string;
    payment_date: string;
    items?: Array<{
      id: number;
      label: string;
      amount: number;
      subtotal_amount: number;
      quantity: number;
      item_type: string;
    }>;
    at_closing_items?: Array<{
      id: number;
      label: string;
      amount: number;
      subtotal_amount: number;
      quantity: number;
      item_type: string;
      apportionments?: Array<{
        id: number;
        party_type: string;
        percentage: number;
        amount: number;
      }>;
    }>;
    total_prepaid?: number;
    total_at_closing?: number;
    management_company_total?: number;
    association_total?: number;
    remaining_balance: number;
  };
  user: {
    name: string;
    email: string;
    job_title: string;
    phone_number: string;
  };
  // Page settings extracted from HTML comments
  pageSettings?: PageSettings;
  // Dynamic section mapping for direct access (e.g., layout.header.buyer.value)
  [sectionName: string]: any;
  // Array structure for looping
  subsections?: Array<{
    title: string;
    fields: Array<{
      label: string;
      value: string;
    }>;
  }>;
  content?: string; // Add content property for the layout
  date: {
    now: string;
  };
}

/**
 * Extract page settings from HTML comments in template content
 */
function extractPageSettingsFromHtml(html: string): PageSettings {
  const pageSettings: PageSettings = {};

  try {
    // Look for PAGE_SETTINGS block
    const pageSettingsMatch = html.match(
      /<!-- PAGE_SETTINGS_START -->([\s\S]*?)<!-- PAGE_SETTINGS_END -->/i
    );

    if (pageSettingsMatch) {
      const settingsContent = pageSettingsMatch[1];

      // Extract border settings
      const borderMatch = settingsContent.match(
        /<!-- BORDER:\s*(\{[^}]+\})\s*-->/i
      );
      if (borderMatch) {
        try {
          const borderSettings = JSON.parse(borderMatch[1]) as BorderSettings;
          pageSettings.border = borderSettings;
        } catch (error) {
          console.warn("Failed to parse border settings:", error);
        }
      }

      // Extract footer settings
      const footerMatch = settingsContent.match(
        /<!-- FOOTER:\s*(\{[^}]+\})\s*-->/i
      );
      if (footerMatch) {
        try {
          const footerSettings = JSON.parse(footerMatch[1]) as FooterSettings;
          pageSettings.footer = footerSettings;
        } catch (error) {
          console.warn("Failed to parse footer settings:", error);
        }
      }

      // Extract page info settings (array of elements)
      const pageInfoMatch = settingsContent.match(
        /<!-- PAGE_INFO:\s*(\[[\s\S]*?\])\s*-->/i
      );
      if (pageInfoMatch) {
        try {
          const pageInfoElements = JSON.parse(
            pageInfoMatch[1]
          ) as PageInfoElement[];
          pageSettings.pageInfo = pageInfoElements;
        } catch (error) {
          console.warn("Failed to parse page info settings:", error);
        }
      }
    }
  } catch (error) {
    console.warn("Error extracting page settings from HTML:", error);
  }

  return pageSettings;
}

/**
 * Fetch management company data by ID
 */
async function fetchManagementCompanyData(
  managementCompanyId: number
): Promise<ManagementCompanyData | null> {
  try {
    const company = await managementCompanyApi.getById(managementCompanyId);
    if (company) {
      return {
        id: company.id,
        name: company.name,
        address: company.address,
        city: company.city,
        state: company.state,
        zip_code: company.zip_code,
        phone: company.phone,
        email: company.email,
        logo: EMAIL_LOGO_URL,
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching management company data:", error);
    return null;
  }
}

/**
 * Fetch association data by ID
 */
async function fetchAssociationData(
  associationId: number
): Promise<AssociationData | null> {
  try {
    const association = await associationApi.getById(associationId);
    if (association) {
      return {
        id: association.id,
        name: association.name,
        management_company_id: association.management_company_id,
        billing_address: association.billing_address,
        billing_city: association.billing_city,
        billing_state: association.billing_state,
        billing_zip: association.billing_zip,
        phone: association.phone,
        email: association.email,
      };
    }
    return null;
  } catch (error) {
    console.error("Error fetching association data:", error);
    return null;
  }
}

/**
 * Fetch property data by ID using the property API
 */
async function fetchPropertyData(
  propertyId: number
): Promise<PropertyData | null> {
  try {
    // Import the property API dynamically to avoid circular dependency issues

    const property = await propertyApi.getById(propertyId);
    console.log("PROPERTY RESULT", property);
    if (property) {
      return {
        id: property.id,
        address: property.address,
        city: property.city,
        state: property.state,
        zip_code: property.zip_code,
        suite_unit: property.suite_unit,
        association_id: property.association_id,
        latitude: property.latitude,
        longitude: property.longitude,
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching property data:", error);
    return null;
  }
}

/**
 * Fetch order data by order document
 */
async function fetchOrderData(orderId: number): Promise<OrderData | null> {
  if (!orderId) {
    console.error("OrderDocument does not have order_id");
    return null;
  }

  try {
    const order = await orderApi.getOrder(orderId);

    if (order) {
      return {
        id: order.id,
        association_id: order.association_id,
        property_id: order.property_id,
        customer_id: order.customer_id,
        created_at: order.created_at,
        status: order.status,
        expected_delivery_date: order.expected_delivery_date,
        processing_type: order.processing_type,
        items: order.items?.map((item) => ({
          id: item.id,
          label: item.label || "",
          amount: item.amount,
          subtotal_amount: item.subtotal_amount,
          quantity: item.quantity,
          item_type: item.item_type,
        })),
        at_closing_items: order.at_closing_items?.map((item) => ({
          id: item.id,
          label: item.label || "",
          amount: item.amount,
          subtotal_amount: item.subtotal_amount,
          quantity: item.quantity,
          item_type: item.item_type,
          apportionments: item.apportionments,
        })),
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching order data:", error);
    return null;
  }
}

/**
 * Fetch template content from CMS based on template ID
 */
async function fetchTemplateContent(
  templateId: number
): Promise<TemplateContent> {
  const SUPABASE_URL = config.SUPABASE_URL;

  try {
    // Fetch the template folder structure
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/cms-content/tree`
    );
    if (!response.ok) {
      throw new Error("Failed to fetch template structure");
    }

    const allContent = await response.json();

    // Find the template folder by ID
    const templateFolder = allContent.find(
      (item: any) => item.id === templateId
    );
    if (!templateFolder) {
      throw new Error(`Template with ID ${templateId} not found`);
    }

    // Find child components (layout, pages)
    const templateComponents = allContent.filter(
      (item: any) => item.parent_id === templateId
    );

    console.log(
      "Template components found:",
      templateComponents.map((c) => ({ id: c.id, name: c.name, type: c.type }))
    );

    const templateContent: TemplateContent = { pages: [] };

    // Fetch content for each component
    for (const component of templateComponents) {
      // The "PDF form" node names a form to fill (see pdfFormTemplate.ts); it
      // is not a layout, even though it is a template node.
      if (String(component.name ?? "").trim().toLowerCase() === "pdf form") {
        continue;
      }

      console.log(
        "Processing component:",
        component.name,
        "Type:",
        component.type,
        "ID:",
        component.id
      );

      const translationResponse = await fetch(
        `${SUPABASE_URL}/functions/v1/cms-content/${component.id}/translations`
      );

      if (translationResponse.ok) {
        const translations = await translationResponse.json();
        const englishTranslation = translations.find(
          (t: any) => t.language_code === "en"
        );

        console.log(
          "Translation found:",
          !!englishTranslation,
          "Has HTML:",
          !!englishTranslation?.html_content
        );

        // Match layout by name (case insensitive) or type
        if (
          (component.name.toLowerCase() === "layout" ||
            component.type === "template") &&
          englishTranslation?.html_content &&
          !templateContent.layout
        ) {
          templateContent.layout = englishTranslation.html_content;
          console.log("Layout content loaded from:", component.name);
        } else if (
          component.name.toLowerCase() === "pages" ||
          component.type === "navigation"
        ) {
          // For pages folder, get all child pages
          const pageItems = allContent.filter(
            (item: any) => item.parent_id === component.id
          );

          console.log(
            "Page items found:",
            pageItems.map((p) => ({ id: p.id, name: p.name, type: p.type }))
          );

          for (const pageItem of pageItems) {
            console.log(
              "Processing page item:",
              pageItem.name,
              "ID:",
              pageItem.id
            );

            const pageResponse = await fetch(
              `${SUPABASE_URL}/functions/v1/cms-content/${pageItem.id}/translations`
            );

            if (pageResponse.ok) {
              const pageTranslations = await pageResponse.json();
              const pageTranslation = pageTranslations.find(
                (t: any) => t.language_code === "en"
              );

              if (pageTranslation?.html_content) {
                templateContent.pages.push(pageTranslation.html_content);
                console.log("Page content added:", pageItem.name);
              } else {
                console.log("No HTML content for page:", pageItem.name);
              }
            } else {
              console.log(
                "Failed to fetch page translations for:",
                pageItem.name
              );
            }
          }
        }
      } else {
        console.log(
          "Failed to fetch translations for component:",
          component.name
        );
      }
    }

    return templateContent;
  } catch (error) {
    console.error("Error fetching template content:", error);
    throw new Error(
      `Failed to fetch template content: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Fetch user data using Supabase client by auth_user_id
 */
async function fetchUserByAuthId(authUserId: string): Promise<{
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  job_title: string | null;
  phone_number: string | null;
} | null> {
  try {
    const { data, error } = await supabase
      .from("users")
      .select("id, email, first_name, last_name, role, job_title, phone_number")
      .eq("auth_user_id", authUserId)
      .single();

    if (error) {
      console.error("Error fetching user by auth_user_id:", error);
      return null;
    }

    if (!data) {
      console.error("No user found with auth_user_id:", authUserId);
      return null;
    }

    return {
      id: data.id,
      email: data.email,
      firstName: data.first_name,
      lastName: data.last_name,
      role: data.role || "Manager",
      job_title: data.job_title,
      phone_number: data.phone_number,
    };
  } catch (error) {
    console.error("Error in fetchUserByAuthId:", error);
    return null;
  }
}

/**
 * Process Liquid template with variables using the proper Liquid engine
 */
async function processLiquidTemplate(
  template: string,
  variables: TemplateVariables
): Promise<string> {
  try {
    // Initialize Liquid engine
    const engine = new Liquid({
      strictVariables: false, // Allow undefined variables to be empty
      strictFilters: false, // Allow undefined filters
    });

    // Add custom number_format filter
    engine.registerFilter(
      "number_format",
      (value: any, decimals: number = 2) => {
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        return num.toFixed(decimals);
      }
    );

    // Add custom money filter for currency formatting
    engine.registerFilter("money", (value: any) => {
      const num = parseFloat(value);
      if (isNaN(num)) return value;
      return num.toFixed(2);
    });

    // Render the template with variables
    const rendered = await engine.parseAndRender(template, variables);
    return rendered;
  } catch (error) {
    console.error("Error processing Liquid template:", error);
    console.error("Template:", template);
    console.error("Variables:", JSON.stringify(variables, null, 2));

    // Fallback: return the original template if there's an error
    return template;
  }
}

/**
 * Prepare variables for template processing
 */
function prepareTemplateVariables(
  orderDocument: OrderDocument & { remaining_balance: number },
  processedSections: Array<{
    id: number;
    title: string;
    subsections: Array<{
      id: number;
      name?: string;
      title: string;
      fields: Array<{
        id: number;
        name?: string;
        title: string;
        value: string;
        comment: string;
        value_source: string;
        referenced_field_id?: number;
      }>;
    }>;
  }>,
  associationData: AssociationData,
  managementCompanyData: ManagementCompanyData,
  propertyData: PropertyData,
  orderData: OrderData | null,
  userData: {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    job_title?: string | null;
    phone_number?: string | null;
  } | null,
  templateContent?: string
): TemplateVariables {
  const now = new Date();

  // Extract page settings from template content if provided
  const pageSettings = templateContent
    ? extractPageSettingsFromHtml(templateContent)
    : {};

  // Create base variables
  const baseVariables: TemplateVariables = {
    document: {
      title: orderDocument.document?.name || "Document",
      name: orderDocument.document?.name || "Document",
      date: now.toLocaleDateString(),
      issuance_date: now.toLocaleDateString(),
    },
    association: {
      name: associationData?.name || "",
      address: associationData?.billing_address || "",
      city: associationData?.billing_city || "",
      state: associationData?.billing_state || "",
      zip_code: associationData?.billing_zip || "",
      phone: associationData?.phone || "",
      email: associationData?.email || "",
    },
    management: {
      name: managementCompanyData?.name || "",
      logo:
        managementCompanyData?.logo ||
        EMAIL_LOGO_URL,
      address: managementCompanyData?.address || "",
      city: managementCompanyData?.city || "",
      state: managementCompanyData?.state || "",
      zip_code: managementCompanyData?.zip_code || "",
      phone: managementCompanyData?.phone || "",
      email: managementCompanyData?.email || "",
    },
    property: propertyData,
    order: {
      id: orderDocument.order_id,
      created_at: now.toLocaleDateString(),
      estimated_closing_date: now.toLocaleDateString(),
      expected_delivery_date: orderData.expected_delivery_date,
      completion_date: now.toLocaleDateString(),
      payment_date: now.toLocaleDateString(),
      items: orderData?.items || [],
      at_closing_items: orderData?.at_closing_items || [],
      total_prepaid:
        orderData?.items?.reduce(
          (total, item) => total + (item.subtotal_amount || 0),
          0
        ) || 0,
      total_at_closing:
        orderData?.at_closing_items?.reduce(
          (total, item) => total + (item.subtotal_amount || 0),
          0
        ) || 0,
      management_company_total:
        orderData?.at_closing_items?.reduce((total, item) => {
          const managementAmount =
            item.apportionments?.find(
              (app) => app.party_type === "management_company"
            )?.amount || 0;
          return total + managementAmount;
        }, 0) || 0,
      association_total:
        orderData?.at_closing_items?.reduce((total, item) => {
          const associationAmount =
            item.apportionments?.find((app) => app.party_type === "association")
              ?.amount || 0;
          return total + associationAmount;
        }, 0) || 0,
      remaining_balance: orderDocument.remaining_balance || 0,
    },
    user: {
      name: `${userData.firstName} ${userData.lastName}`,
      email: userData?.email,
      job_title: userData?.job_title,
      phone_number: userData?.phone_number,
    },
    pageSettings,
    date: {
      now: `${String(now.getMonth() + 1).padStart(2, "0")}/${String(
        now.getDate()
      ).padStart(2, "0")}/${now.getFullYear()}`,
    },
  };

  // Create dynamic section mappings for direct access (nested structure)
  processedSections.forEach((section) => {
    const sectionName = section.title.toLowerCase().replace(/\s+/g, "_");
    baseVariables[sectionName] = {};

    section.subsections.forEach((subsection) => {
      const subsectionName =
        subsection.name || subsection.title.toLowerCase().replace(/\s+/g, "_");
      baseVariables[sectionName][subsectionName] = {};

      subsection.fields.forEach((field) => {
        const fieldName =
          field.name || field.title.toLowerCase().replace(/\s+/g, "_");
        baseVariables[sectionName][subsectionName][fieldName] = {
          value: field.value || "",
          comment: field.comment || "",
          title: field.title,
          name: field.name,
        };
      });
    });
  });

  // Create flatter structure for template access: subsection.fieldName = sectionName.subsectionName.fieldName.value
  processedSections.forEach((section) => {
    section.subsections.forEach((subsection) => {
      // Use the subsection name (or title) as the top-level variable name
      // Clean up the name to make it template-friendly
      const subsectionVarName = (subsection.name || subsection.title)
        .toLowerCase()
        .replace(/[^a-zA-Z0-9_]/g, "_") // Replace special chars with underscore
        .replace(/_+/g, "_") // Replace multiple underscores with single
        .replace(/^_|_$/g, ""); // Remove leading/trailing underscores

      // Initialize subsection object if it doesn't exist
      if (!baseVariables[subsectionVarName]) {
        baseVariables[subsectionVarName] = {};
      }

      subsection.fields.forEach((field) => {
        // Use the field name (or title) as the field variable name
        // Clean up the name to make it template-friendly
        const fieldVarName = (field.name || field.title)
          .toLowerCase()
          .replace(/[^a-zA-Z0-9_]/g, "_") // Replace special chars with underscore
          .replace(/_+/g, "_") // Replace multiple underscores with single
          .replace(/^_|_$/g, ""); // Remove leading/trailing underscores

        // Set the field value directly accessible as subsection.fieldName
        baseVariables[subsectionVarName][fieldVarName] = field.value || "";
        // Log the variable mapping for debugging
        console.log(
          `Template variable created: ${subsectionVarName}.${fieldVarName} = "${field.value}"`
        );
      });
    });
  });

  // Create subsections array for looping (used in content template)
  const contentSection = processedSections.find(
    (s) => s.title.toLowerCase() === "content"
  );
  if (contentSection) {
    baseVariables.subsections = contentSection.subsections.map(
      (subsection) => ({
        title: subsection.title,
        fields: subsection.fields.map((field) => ({
          label: field.title,
          value: field.value || "",
          comment: field.comment,
        })),
      })
    );
  }

  console.log("VARIABLES", baseVariables);
  console.log("=== End Template Variables ===");

  return baseVariables;
}

/**
 * Extract header content from the full HTML and render it to a canvas
 */
async function extractHeaderCanvas(
  html: string
): Promise<HTMLCanvasElement | null> {
  console.log("Attempting to extract header content and render to canvas...");

  // Method 1: Extract the header section from the HTML
  const headerMatch = html.match(/<header[^>]*>([\s\S]*?)<\/header>/i);
  if (!headerMatch) {
    console.log("No header content found");
    return null;
  }

  const headerAttrs = headerMatch[0].substring(7, headerMatch[0].indexOf(">"));
  const extractedHeader = `<header${headerAttrs}>${headerMatch[1]}</header>`;
  console.log("Found header tag, rendering to canvas");

  // Create a temporary div for the header
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = extractedHeader;
  tempDiv.style.position = "absolute";
  tempDiv.style.left = "-9999px";
  tempDiv.style.width = "800px"; // Same width as main content
  tempDiv.style.fontFamily = "Arial, sans-serif";
  tempDiv.style.boxSizing = "border-box";
  tempDiv.style.background = "#ffffff";
  tempDiv.style.padding = "7px";
  document.body.appendChild(tempDiv);

  try {
    // Stored file refs (signatures, uploads) are private: sign them first.
    await resolveStorageImagesIn(tempDiv);

    // Wait for images to load in header (with error handling)
    await Promise.all(
      Array.from(tempDiv.querySelectorAll("img")).map((img) => {
        return new Promise((resolve) => {
          if (img.complete) {
            resolve(null);
          } else {
            img.onload = () => resolve(null);
            img.onerror = (error) => {
              console.warn(
                "Failed to load header image in template PDF:",
                img.src,
                error
              );
              // Hide the failed image instead of failing the entire PDF generation
              img.style.display = "none";
              resolve(null);
            };
          }
        });
      })
    );

    // Convert header to canvas
    const headerCanvas = await html2canvas(tempDiv, {
      useCORS: true,
      allowTaint: true,
      background: "#ffffff",
      width: tempDiv.offsetWidth,
      height: tempDiv.offsetHeight,
      logging: false,
    });

    console.log("Header canvas created:", {
      width: headerCanvas.width,
      height: headerCanvas.height,
    });

    return headerCanvas;
  } catch (error) {
    console.error("Error creating header canvas:", error);
    return null;
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
}

/**
 * Extract header content from the full HTML (legacy function for fallback)
 */
function extractHeaderContent(html: string): string {
  console.log("Attempting to extract header content...");

  // Method 1: Extract the header section from the HTML
  const headerMatch = html.match(/<header[^>]*>([\s\S]*?)<\/header>/i);
  if (headerMatch) {
    const headerAttrs = headerMatch[0].substring(
      7,
      headerMatch[0].indexOf(">")
    );
    const extractedHeader = `<header${headerAttrs}>${headerMatch[1]}</header>`;
    console.log("Found header tag, extracted content");
    return extractedHeader;
  }

  console.log("No header content extracted");
  return "";
}

/**
 * Find a safe break point to avoid cutting content by analyzing pixel data
 */
function findSafeBreakPoint(
  canvas: HTMLCanvasElement,
  startY: number,
  maxHeight: number
): number {
  const ctx = canvas.getContext("2d");
  if (!ctx) return Math.min(startY + maxHeight, canvas.height);

  const endY = Math.min(startY + maxHeight, canvas.height);
  const searchRange = Math.min(200, maxHeight * 0.25); // Increased search range: 25% of height or 200px

  // Start from the ideal end point and work backwards to find a good break
  for (let y = endY; y > endY - searchRange && y > startY; y -= 2) {
    // Smaller step for better detection
    // Get a horizontal line of pixels to analyze
    const imageData = ctx.getImageData(0, y, canvas.width, 1);
    const pixels = imageData.data;

    let whitePixelCount = 0;
    let totalPixels = 0;

    // Check if this line is mostly white (relaxed criteria)
    for (let i = 0; i < pixels.length; i += 4) {
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];

      totalPixels++;
      // More lenient white detection
      if (r >= 240 && g >= 240 && b >= 240) {
        whitePixelCount++;
      }
    }

    // If 90% or more pixels are white, consider it a good break point
    if (whitePixelCount / totalPixels >= 0.9) {
      return y;
    }
  }

  // If no good break point found, use the full available height
  // Use essentially all available space
  return Math.min(startY + maxHeight, canvas.height);
}

/**
 * Draw page border on PDF based on border settings
 */
function drawPageBorder(
  pdf: jsPDF,
  borderSettings: BorderSettings,
  pageWidth: number,
  pageHeight: number,
  borderMargin: number,
  pageNumberSpace: number
) {
  if (!borderSettings.enabled) return;

  const borderWidth = parseFloat(borderSettings.width) || 1;

  pdf.setLineWidth(borderWidth);
  pdf.setDrawColor(0, 0, 0); // Black border

  // Draw rectangle border
  pdf.rect(
    borderMargin,
    borderMargin,
    pageWidth - 2 * borderMargin,
    pageHeight - 2 * borderMargin - pageNumberSpace
  );
}

/**
 * Add generic page information elements with different alignments
 */
function addPageInfo(
  pdf: jsPDF,
  pageInfoElements: PageInfoElement[],
  pageNum: number,
  totalPages: number,
  pageWidth: number,
  pageHeight: number,
  borderMargin: number,
  pageNumberSpace: number,
  orderId?: number
) {
  const footerY = pageHeight - pageNumberSpace / 2;

  pageInfoElements.forEach((element) => {
    // Process page info text with liquid-like variables
    let pageInfoText = element.text
      .replace(/\{\{\s*page\.current\s*\}\}/g, pageNum.toString())
      .replace(/\{\{\s*page\.total\s*\}\}/g, totalPages.toString())
      .replace(/\{\{\s*order\.id\s*\}\}/g, orderId?.toString() || "");

    const fontSize = parseInt(element.fontSize) || 12;
    pdf.setFontSize(fontSize);
    pdf.setTextColor(0, 0, 0); // Black text color to match image

    const textWidth = pdf.getTextWidth(pageInfoText);

    let pageInfoX: number;
    switch (element.alignment) {
      case "left":
        pageInfoX = borderMargin;
        break;
      case "center":
        pageInfoX = (pageWidth - textWidth) / 2;
        break;
      case "right":
      default:
        pageInfoX = pageWidth - borderMargin - textWidth;
        break;
    }

    pdf.text(pageInfoText, pageInfoX, footerY);
  });
}

/**
 * Add page footer based on footer settings
 */
function addPageFooter(
  pdf: jsPDF,
  footerSettings: FooterSettings,
  pageNum: number,
  totalPages: number,
  pageWidth: number,
  pageHeight: number,
  borderMargin: number,
  pageNumberSpace: number,
  orderId?: number
) {
  if (!footerSettings.enabled) return;

  // Process footer text with liquid-like variables
  let footerText = footerSettings.text
    .replace(/\{\{\s*page\.current\s*\}\}/g, pageNum.toString())
    .replace(/\{\{\s*page\.total\s*\}\}/g, totalPages.toString());

  const fontSize = parseInt(footerSettings.fontSize) || 10;
  pdf.setFontSize(fontSize);
  pdf.setTextColor(0, 0, 0); // Black text

  const textWidth = pdf.getTextWidth(footerText);
  const footerY = pageHeight - pageNumberSpace / 2;

  let footerX: number;
  switch (footerSettings.alignment) {
    case "left":
      footerX = borderMargin;
      break;
    case "center":
      footerX = (pageWidth - textWidth) / 2;
      break;
    case "right":
    default:
      footerX = pageWidth - borderMargin - textWidth;
      break;
  }

  pdf.text(footerText, footerX, footerY);
}

/**
 * Convert HTML to PDF with optimized file size, page borders, page numbers, and automatic page overflow handling
 */
async function htmlToPdf(
  html: string,
  pageSettings: PageSettings,
  orderId: number,
  processedLayoutHtml: string
): Promise<Blob> {
  // Extract header canvas from processed layout HTML (with variables filled but only layout structure)
  const headerCanvas = await extractHeaderCanvas(processedLayoutHtml);
  console.log("Extracted header canvas:", headerCanvas ? "Found" : "Not found");

  // Calculate header height in PDF units based on actual header canvas size
  let headerHeight = 0;
  if (headerCanvas) {
    // Calculate header height in mm based on canvas dimensions
    // Assuming canvas width is 800px and PDF content width maintains aspect ratio
    const canvasWidthMm = 190; // Approximate content width in mm (210 - 2*10 margins)
    const headerHeightMm =
      (headerCanvas.height * canvasWidthMm) / headerCanvas.width;
    headerHeight = Math.min(headerHeightMm, 30); // Cap at 30mm to prevent excessive header size
    console.log("Header dimensions:", {
      canvasWidth: headerCanvas.width,
      canvasHeight: headerCanvas.height,
      calculatedHeightMm: headerHeightMm,
      finalHeaderHeight: headerHeight,
    });
  }

  // Split HTML by page breaks - support multiple page break patterns
  const pageBreakRegex =
    /<div[^>]*style="[^"]*page-break-[^"]*always[^"]*"[^>]*>[\s\S]*?<\/div>|<div[^>]*class="[^"]*page-break[^"]*"[^>]*>[\s\S]*?<\/div>/gi;
  const pages = html.split(pageBreakRegex).filter((page) => page.trim());

  const pdf = new jsPDF("p", "mm", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  // Define border margins and content area
  const borderMargin = 10; // mm from page edge
  const contentMargin = 9; // mm from border to content
  const pageNumberSpace = 8; // mm space for page number outside border

  // Calculate content area dimensions (adjust for header space)
  const contentWidth = pdfWidth - 2 * contentMargin;
  const maxContentHeight =
    pdfHeight - 2 * contentMargin - pageNumberSpace - headerHeight;

  let totalPdfPages = 0; // Track total PDF pages for page numbering

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    let pageHtml = pages[pageIndex].trim();
    if (!pageHtml) continue;

    // Note: Header will be added as canvas overlay later, not as HTML

    // Create a temporary div for this logical page
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = pageHtml;
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    tempDiv.style.width = "800px"; // Fixed pixel width for consistent rendering
    tempDiv.style.padding = "7px";
    tempDiv.style.boxSizing = "border-box";
    tempDiv.style.background = "#ffffff";
    document.body.appendChild(tempDiv);

    try {
      // Stored file refs (signatures, uploads) are private: sign them first.
      await resolveStorageImagesIn(tempDiv);

      // Wait for images to load (with error handling)
      await Promise.all(
        Array.from(tempDiv.querySelectorAll("img")).map((img) => {
          return new Promise((resolve) => {
            if (img.complete) {
              resolve(null);
            } else {
              img.onload = () => resolve(null);
              img.onerror = (error) => {
                console.warn(
                  "Failed to load image in template PDF:",
                  img.src,
                  error
                );
                // Hide the failed image instead of failing the entire PDF generation
                img.style.display = "none";
                resolve(null);
              };
            }
          });
        })
      );

      // Convert this logical page to canvas
      const fullCanvas = await html2canvas(tempDiv, {
        useCORS: true,
        allowTaint: true,
        background: "#ffffff",
        width: tempDiv.offsetWidth,
        height: tempDiv.offsetHeight,
        logging: false,
      });

      // Calculate pixel height that corresponds to PDF content area
      // Canvas width is 800px (fixed), so calculate scale ratio
      const canvasToMmRatio = contentWidth / fullCanvas.width; // mm per pixel
      const maxContentHeightPixels = maxContentHeight / canvasToMmRatio; // convert mm to pixels

      // Calculate header height in pixels if we have a header
      const headerHeightPixels = headerCanvas ? headerCanvas.height : 0;

      // Available content height per page in pixels (excluding header space)
      const availableContentHeightPixels =
        maxContentHeightPixels - headerHeightPixels;

      console.log("PDF page calculations:", {
        canvasSize: `${fullCanvas.width}x${fullCanvas.height}px`,
        maxContentHeight: `${maxContentHeight}mm (${maxContentHeightPixels}px)`,
        headerHeight: `${headerHeightPixels}px`,
        availableContentHeight: `${availableContentHeightPixels}px`,
      });

      const totalCanvasHeight = fullCanvas.height;
      let currentY = 0;
      let sliceCount = 0;

      // Split the canvas into page-sized slices
      while (currentY < totalCanvasHeight) {
        // Calculate remaining height for this slice
        const remainingHeight = totalCanvasHeight - currentY;

        // Skip if there's very little content left (less than 50px)
        if (remainingHeight < 50) {
          break;
        }

        // Add new PDF page if not the very first page
        if (totalPdfPages > 0) {
          pdf.addPage();
        }

        // Check if we need to add header to this page (skip first page unless explicitly first page has header)
        const needsHeader = totalPdfPages > 0 && headerCanvas;
        const currentPageAvailableHeight = needsHeader
          ? availableContentHeightPixels
          : maxContentHeightPixels;

        // Find a safe break point to avoid cutting content
        let currentSliceHeight;
        if (remainingHeight <= currentPageAvailableHeight) {
          // Last slice or fits in current page, use all remaining content
          currentSliceHeight = remainingHeight;
        } else {
          // Find a safe break point within available space
          currentSliceHeight =
            findSafeBreakPoint(
              fullCanvas,
              currentY,
              currentPageAvailableHeight
            ) - currentY;

          // Ensure we make progress (minimum slice height to avoid infinite loops)
          if (currentSliceHeight < currentPageAvailableHeight * 0.1) {
            currentSliceHeight = Math.min(
              currentPageAvailableHeight * 0.95,
              remainingHeight
            );
          }
        }

        // Log only when there are potential issues
        if (
          sliceCount > 10 ||
          currentSliceHeight < currentPageAvailableHeight * 0.5
        ) {
          console.log("PDF slice info:", {
            slice: sliceCount,
            position: `${currentY}/${totalCanvasHeight}px`,
            sliceHeight: `${currentSliceHeight}px`,
            hasHeader: needsHeader,
          });
        }

        // Create a temporary canvas for this slice
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = fullCanvas.width;

        // Calculate actual header height for this page
        let actualHeaderHeight = 0;
        if (needsHeader) {
          actualHeaderHeight = headerCanvas!.height;
        }

        sliceCanvas.height = currentSliceHeight + actualHeaderHeight;
        const sliceCtx = sliceCanvas.getContext("2d");

        if (sliceCtx) {
          // Fill with white background first
          sliceCtx.fillStyle = "#ffffff";
          sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);

          let contentYOffset = 0;

          // Add header canvas at the top if needed
          if (needsHeader) {
            // Draw header at its original size
            sliceCtx.drawImage(
              headerCanvas!,
              0,
              0, // Source x, y
              headerCanvas!.width,
              headerCanvas!.height, // Source width, height
              0,
              0, // Destination x, y
              headerCanvas!.width,
              headerCanvas!.height // Destination width, height (no scaling)
            );

            contentYOffset = actualHeaderHeight;
          }

          // Copy the slice from the full canvas below the header
          sliceCtx.drawImage(
            fullCanvas,
            0,
            currentY, // Source x, y
            fullCanvas.width,
            currentSliceHeight, // Source width, height
            0,
            contentYOffset, // Destination x, y (offset by header height)
            fullCanvas.width,
            currentSliceHeight // Destination width, height (no scaling)
          );

          // Calculate the height for this slice in the PDF - maintain proper aspect ratio
          const sliceImgHeight =
            (sliceCanvas.height * contentWidth) / sliceCanvas.width;

          // DON'T limit the height - let it render at natural size to prevent stretching
          // The slicing logic above already ensures it fits within page boundaries
          const finalSliceHeight = sliceImgHeight;

          // Only log if there might be scaling issues
          if (Math.abs(sliceImgHeight - finalSliceHeight) > 1) {
            console.log("PDF image scaling:", {
              canvasHeight: sliceCanvas.height,
              scaledHeight: sliceImgHeight,
              finalHeight: finalSliceHeight,
            });
          }

          // Use JPEG format with compression for smaller file size
          const imgData = sliceCanvas.toDataURL("image/jpeg", 1);

          // Add the slice to the PDF (header is now part of the image canvas)
          pdf.addImage(
            imgData,
            "JPEG",
            contentMargin,
            contentMargin,
            contentWidth,
            finalSliceHeight
          );
        }

        // Draw page border based on settings
        if (pageSettings?.border?.enabled) {
          drawPageBorder(
            pdf,
            pageSettings.border,
            pdfWidth,
            pdfHeight,
            borderMargin,
            pageNumberSpace
          );
        }

        totalPdfPages++;
        currentY += currentSliceHeight;
        sliceCount++;

        // Safety check to prevent infinite loops
        if (sliceCount > 50) {
          console.warn(
            "PDF generation stopped after 50 slices per logical page to prevent infinite loop"
          );
          break;
        }
      }
    } finally {
      // Clean up
      document.body.removeChild(tempDiv);
    }
  }

  // Add page footers to all pages based on settings
  const totalPages = totalPdfPages;
  for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
    // Go to the specific page
    pdf.setPage(pageNum);

    // Priority order for footer rendering:
    // 1. Regular footer settings
    // 2. Custom page info elements
    // 3. Default page numbering

    if (pageSettings?.footer?.enabled) {
      // Use regular footer
      addPageFooter(
        pdf,
        pageSettings.footer,
        pageNum,
        totalPages,
        pdfWidth,
        pdfHeight,
        borderMargin,
        pageNumberSpace,
        orderId
      );
    } else if (pageSettings?.pageInfo && pageSettings.pageInfo.length > 0) {
      // Use custom page info elements
      addPageInfo(
        pdf,
        pageSettings.pageInfo,
        pageNum,
        totalPages,
        pdfWidth,
        pdfHeight,
        borderMargin,
        pageNumberSpace,
        orderId
      );
    } else {
      // Default page numbering if no footer settings
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0); // Black text
      const pageText = `Page ${pageNum} of ${totalPages}`;
      const textWidth = pdf.getTextWidth(pageText);
      const pageNumberX = pdfWidth - borderMargin - textWidth;
      const pageNumberY = pdfHeight - pageNumberSpace / 2;

      pdf.text(pageText, pageNumberX, pageNumberY);
    }
  }

  // Convert to blob
  const pdfBlob = pdf.output("blob");
  return pdfBlob;
}

/**
 * Generate a meaningful filename for the PDF document
 */
function generatePdfFilename(
  orderDocument: OrderDocument,
  associationData?: AssociationData,
  managementCompanyData?: ManagementCompanyData,
  propertyData?: PropertyData
): string {
  const date = new Date();
  const dateString = date.toISOString().split("T")[0]; // YYYY-MM-DD format

  // Start with document name or type
  let filename =
    orderDocument.document?.name || `Document_${orderDocument.document_id}`;

  // Clean up the document name to be filename-safe
  filename = filename.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");

  // Add association name if available
  if (associationData?.name) {
    const cleanAssociationName = associationData.name
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "_");
    filename = `${cleanAssociationName}_${filename}`;
  }

  // Add property address if available
  if (propertyData?.address) {
    const cleanPropertyAddress = propertyData.address
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "_")
      .substring(0, 50); // Limit length
    filename = `${filename}_${cleanPropertyAddress}`;
  }

  // Add date
  filename = `${filename}_${dateString}`;

  // Add management company if different from association and available
  if (
    managementCompanyData?.name &&
    managementCompanyData.name !== associationData?.name
  ) {
    const cleanManagementName = managementCompanyData.name
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "_");
    filename = `${filename}_${cleanManagementName}`;
  }

  // Ensure reasonable length (max 200 characters before .pdf extension)
  if (filename.length > 200) {
    filename = filename.substring(0, 200);
  }

  return `${filename}.pdf`;
}

export type TemplatePdfParams = {
  orderDocument: OrderDocument & { remaining_balance: number };
  processedSections: Array<{
    id: number;
    title: string;
    subsections: Array<{
      id: number;
      name?: string;
      title: string;
      fields: Array<{
        id: number;
        name?: string;
        title: string;
        value: string;
        comment: string;
        value_source: string;
        referenced_field_id?: number;
      }>;
    }>;
  }>;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    job_title: string;
    phone_number: string;
  };
};

/**
 * The PDF-form path: every processed line carries the name of the form field
 * it came from, and the spec says which box of the official PDF that name
 * fills. The return-by date is the order's promised delivery date.
 */
async function generateFilledFormPdf(
  orderDocument: TemplatePdfParams["orderDocument"],
  processedSections: TemplatePdfParams["processedSections"],
  pdfForm: PdfFormTemplate
): Promise<{ pdfBlob: Blob; filename: string }> {
  const answers: Record<string, string> = {};
  for (const section of processedSections) {
    for (const subsection of section.subsections ?? []) {
      for (const field of subsection.fields ?? []) {
        if (!field.name) continue;
        const value = field.value == null ? "" : String(field.value);
        if (value.trim() !== "") answers[field.name] = value;
      }
    }
  }

  const [orderData, associationData] = await Promise.all([
    fetchOrderData(orderDocument.order_id),
    fetchAssociationData(orderDocument.association_id),
  ]);

  // The node's file name picks the fill routine: each bank's official PDF
  // has its own (AcroForm by name, checkbox pairs, or a drawn overlay).
  const fill = fillerFor(pdfForm.file);
  const { bytes, report } = await fill(
    answers,
    { return_by: formatDate(orderData?.expected_delivery_date) },
    { pdfUrl: pdfForm.url }
  );

  console.log(
    `[pdf form] ${pdfForm.file}: ${report.text} text fields, ${report.boxes} boxes` +
      (report.problems.length ? `, ${report.problems.length} problems` : ""),
    report.problems
  );

  const pdfBlob = new Blob([bytes as BlobPart], { type: "application/pdf" });
  const filename = generatePdfFilename(orderDocument, associationData || undefined);
  return { pdfBlob, filename };
}

/**
 * Main function to generate PDF from template
 */
export async function generateTemplatedPdf({
  orderDocument,
  processedSections,
  user,
}: TemplatePdfParams): Promise<{ pdfBlob: Blob; filename: string }> {
  try {
    console.log("ORDER DOCUMENT", orderDocument);
    // Get document type to find template ID
    if (!orderDocument.document?.document_type_id) {
      throw new Error("Document type ID not found");
    }

    const documentType = await documentTypeApi.getById(
      orderDocument.document.document_type_id
    );

    // The DOCUMENT's own template wins over the type's (8-Sep: "el output es
    // parte del documento, no del document type"). null = inherit the type's
    // template, which is how every existing document keeps behaving.
    const { data: docOverride } = await supabase
      .from("documents")
      .select("template_id")
      .eq("id", orderDocument.document_id)
      .maybeSingle();
    const templateId = docOverride?.template_id ?? documentType.template_id;

    if (!templateId) {
      throw new Error("No template configured for this document or its type");
    }

    // A template folder that carries a "PDF form" node is not rendered from
    // HTML: the official form is filled with the same processed values.
    const pdfForm = await findPdfFormTemplate(templateId);
    if (pdfForm) {
      return generateFilledFormPdf(orderDocument, processedSections, pdfForm);
    }

    // Parallelize independent data fetching operations
    const [orderData, userData, associationData, templateContent] =
      await Promise.all([
        // Fetch order data to get property and association information
        fetchOrderData(orderDocument.order_id),

        // Fetch user data using auth_user_id
        fetchUserByAuthId(user.id),

        // Fetch management company data (conditional)
        fetchAssociationData(orderDocument.association_id),

        // Fetch template content
        fetchTemplateContent(templateId),
      ]);

    // log each
    console.log("Fetched user data:", userData);

    // Fetch property data after orderData is available (depends on orderData.property_id)
    const [propertyData, managementCompanyData] = await Promise.all([
      fetchPropertyData(orderData.property_id),
      fetchManagementCompanyData(associationData.management_company_id),
    ]);

    console.log("Template content fetched:", {
      hasLayout: !!templateContent.layout,
      pagesCount: templateContent.pages.length,
      templateId,
    });

    if (!templateContent.layout) {
      throw new Error("Layout template not found");
    }

    // Prepare variables with all the fetched data
    const variables = prepareTemplateVariables(
      orderDocument,
      processedSections,
      associationData,
      managementCompanyData,
      propertyData,
      orderData,
      userData,
      templateContent.layout
    );

    console.log("Template variables prepared:", variables);

    // Process templates
    let processedPages: string[] = [];

    // Process each page template
    if (templateContent.pages.length > 0) {
      processedPages = await Promise.all(
        templateContent.pages.map((pageTemplate) =>
          processLiquidTemplate(pageTemplate, variables)
        )
      );
    }
    // Combine all content with page breaks
    const combinedContent = processedPages.join(
      '<div style="page-break-before: always;"></div>'
    );

    // Process the layout template with variables (but without content) for header extraction
    const processedLayout = await processLiquidTemplate(
      templateContent.layout,
      {
        ...variables,
        content: "", // Empty content for layout processing
      }
    );

    // Process the main layout with all the processed components
    const finalHtml = await processLiquidTemplate(templateContent.layout, {
      ...variables,
      content: combinedContent,
    });

    // Generate PDF with page settings
    const pdfBlob = await htmlToPdf(
      finalHtml,
      variables.pageSettings,
      orderDocument.order_id,
      processedLayout
    );

    // Generate meaningful filename
    const filename = generatePdfFilename(
      orderDocument,
      associationData || undefined,
      managementCompanyData || undefined,
      propertyData || undefined
    );

    return { pdfBlob, filename };
  } catch (error) {
    console.error("Error in generateTemplatedPdf:", error);
    throw error;
  }
}
